import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import FileUploadButton from 'Components/UI/ActivityFeed/Shared/HouseholdFeed/MessageInput/FileUploadButton';
import TemplateOptionsMenu from 'Components/UI/TemplateOptionsMenu';
import { TemplatesProvider } from 'Context/TemplateContext';
import useUserContext from 'Context/UserContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { noop } from 'Helpers/PropTypes';
import {
  RichTextEditorInputFieldSkeleton,
} from 'Components/ComponentLibrary/Components/Uncategorized/RichTextEditorInputField/components/RichTextEditorInputFieldSkeleton';
import {
  RichTextEditorInputField,
} from 'Components/ComponentLibrary/Components/Uncategorized/RichTextEditorInputField';
import fr from 'facet-rest';
import { FileViewer }
  from 'Components/ComponentLibrary/Components/Uncategorized/FileViewer';
import { QueryVal, removeQuery } from 'Helpers/Query';
import { SendButton } from 'Components/ComponentLibrary/Components/Uncategorized/SendButton';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { DataValueMap } from 'Helpers/Testing';
import useMobileLayout from 'Hooks/useMobileLayout';
import { TEMPLATE_TYPES } from 'Components/UI/TemplateOptionsMenu/utils';
import { AttachmentsRebranded } from 'Components/ComponentLibrary/Components/Uncategorized/AttachmentsRebranded';

const useStyles = buildUseStyles('AgentMessageInput', (theme) => ({
  root: {
    position: 'relative',
  },
  toolbar: {
    display: 'flex',
    bottom: theme.spacing(0.25),
    right: theme.spacing(1),
    position: 'absolute',
    height: theme.spacing(4.5), // 36px is CKEditor toolbar height
  },
  toolbarButton: {
    margin: 0,
  },
  templateButton: {
    padding: theme.spacing(1),
  },
  sendButton: {
    padding: theme.spacing(1),
  },
  fileUploadButton: {
    boxSizing: 'content-box',
    height: 20,
    width: 20,
    padding: theme.spacing(1),
    marginRight: theme.spacing(0.5),
  },
  hidden: {
    display: 'none',
  },
  inputContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
  },
  input: {
    width: '100%',
  },
  readOnlyMessage: {
    padding: theme.spacing(2),
    whiteSpace: 'pre-line',
    textAlign: 'center',
    position: 'absolute',
    // Below is how to center an item vertically in absolute/relative positioning (overtop)
    // Move down 50% of the container height and then move back up 50% of the moved elements height to center perfectly
    top: '50%',
    transform: 'translate(0, -50%)',
    // Above is how to center an item vertically in absolute/relative positioning (overtop)
  },
}));

const AgentMessageInput = ({
  className,
  handleSubmit,
  handleChange,
  onSubjectChange,
  handleAttachmentDelete,
  messageSending,
  messageResource,
  onFileUploadSuccess,
  placeholder,
  readOnly,
  inputMinHeight,
  inputMaxHeight,
  value,
  attachments,
  dataTestAttributes,
  borderless,
  rounded,
  handleTemplateInsert,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const [messageInputLoading, setMessageInputLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = React.useState(null);
  const { agentResource } = useUserContext();
  const { displayErrorMessage } = useSnackbarContext();
  const isMobile = useMobileLayout();

  const hasAttachments = attachments.length > 0;
  const attachmentSelfLink = QueryVal('attachmentSelfLink');
  const disableActions = messageSending || readOnly;
  const disableSend = readOnly || value === '';
  const inputPlaceholder = readOnly ? '' : placeholder;
  const readOnlyMessage = isMobile ? 'This is a read-only message\nStart or reply to a different thread if you want to speak to this member.'
    : 'This is a read-only message. Start or reply to a different thread if you want to speak to this member.';
  const skeletonHeight = inputMinHeight + 50;

  // This appends a template or token that is selected and passed in as HTML
  const appendHTMLTemplate = (content, newSubject, templateID) => {
    const newHtml = value + content;
    handleChange(newHtml);
    onSubjectChange(newSubject);
    handleTemplateInsert(templateID);
  };

  return (
    <Box className={classNames(className, classes.root)}>
      {messageInputLoading && <RichTextEditorInputFieldSkeleton height={skeletonHeight} />}
      <Box
        className={classNames({
          [classes.inputContainer]: !messageInputLoading,
          [classes.hidden]: messageInputLoading,
        })}
        {...dataTestAttributes}
      >
        <RichTextEditorInputField
          className={classes.input}
          onChange={(val) => handleChange(val)}
          onLoaded={() => setMessageInputLoading(false)}
          value={value}
          disabled={readOnly}
          rounded={rounded}
          customConfig={{
            editorplaceholder: inputPlaceholder,
            height: inputMinHeight,
            extraPlugins: 'autogrow',
            autoGrow_minHeight: inputMinHeight,
            autoGrow_maxHeight: inputMaxHeight,
          }}
          borderless={borderless}
        />
        <Box className={classes.toolbar}>
          <TemplatesProvider
            templatesLinkUrl={agentResource?.relationship(fr.relationships.messageTemplates.name).link('related').url()}
            pinnedTemplatesLinkUrl={agentResource?.relationship(fr.relationships.pinnedMessageTemplates.name).link('related').url()}
          >
            <TemplateOptionsMenu
              useLegacyButton
              className={classes.toolbarButton}
              onInsertTemplate={(selectedTemplateResource) => {
                appendHTMLTemplate(selectedTemplateResource.attr('content'), selectedTemplateResource.attr('subject'), selectedTemplateResource.id());
              }}
              onInsertToken={(token) => appendHTMLTemplate(token)}
              templateDraftContent={value}
              templateSubject={messageResource?.attr('subject')}
              templateType={TEMPLATE_TYPES.messageMeetingCombo}
              ButtonProps={{
                disabled: disableActions,
                className: classes.templateButton,
              }}
            />
          </TemplatesProvider>
          <FileUploadButton
            className={classes.toolbarButton}
            messageResource={messageResource}
            onStart={() => messageResource?.relationship(fr.relationships.attachments.name)?.link('related').url()}
            onSuccess={() => onFileUploadSuccess()}
            onFailure={(e) => displayErrorMessage(e, 'Failed to upload file')}
            uploadProgress={uploadProgress}
            setUploadProgress={setUploadProgress}
            disabled={disableActions}
            circularProgress
            ButtonProps={{
              className: classes.fileUploadButton,
            }}
          />
          <SendButton
            className={classNames(classes.sendButton, classes.toolbarButton)}
            onClick={() => handleSubmit()}
            sending={messageSending}
            disabled={disableSend}
            dataTestAttributes={DataValueMap.init('send-message-button').build()}
          />
        </Box>
        {readOnly && (
          <Typography
            className={classes.readOnlyMessage}
            color={'mediumDarkGray'}
            variant={'h6'}
            weight={'light'}
            {...DataValueMap.init('read-only-message').build()}
          >
            {readOnlyMessage}
          </Typography>
        )}
      </Box>
      {hasAttachments && (
        <AttachmentsRebranded
          className={classes.attachments}
          attachmentResources={attachments}
          AttachmentProps={{
            onDelete: handleAttachmentDelete,
          }}
        />
      )}
      <FileViewer
        attachmentResources={attachments}
        isOpen={!!attachmentSelfLink}
        closeModal={() => removeQuery('attachmentSelfLink')}
        eventCategory={'Messages'}
      />
    </Box>
  );
};

AgentMessageInput.defaultProps = {
  className: '',
  messageSending: false,
  handleChange: noop,
  handleSubmit: noop,
  onSubjectChange: noop,
  handleAttachmentDelete: noop,
  onFileUploadSuccess: noop,
  placeholder: 'Type here...',
  readOnly: false,
  inputMinHeight: 90,
  inputMaxHeight: 500,
  messageResource: null,
  value: '',
  attachments: [],
  dataTestAttributes: {},
  borderless: false,
  rounded: false,
  handleTemplateInsert: noop,
};

AgentMessageInput.propTypes = {
  className: PropTypes.string,
  messageSending: PropTypes.bool,
  messageResource: PropTypes.instanceOf(fr.Resource),
  handleChange: PropTypes.func,
  handleSubmit: PropTypes.func,
  onSubjectChange: PropTypes.func,
  handleAttachmentDelete: PropTypes.func,
  onFileUploadSuccess: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  inputMinHeight: PropTypes.number,
  inputMaxHeight: PropTypes.number,
  value: PropTypes.string,
  attachments: PropTypes.array,
  dataTestAttributes: PropTypes.object,
  borderless: PropTypes.bool,
  rounded: PropTypes.bool,
  handleTemplateInsert: PropTypes.func,
};

export { AgentMessageInput };
